import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";

/**
 * RequireAuth — the client-side route guard that replaces business-v2's
 * `middleware.ts` cookie gate.
 *
 * The Next app gated on the `__Secure-session` HttpOnly cookie in edge
 * middleware. JS cannot read that cookie, so the Vite app instead consumes the
 * @bw/shared `useAuth()` session signal (which polls `/bff/auth/me` — the same
 * cookie, observed server-side). While the session is still resolving we render
 * a centered spinner so a logged-in operator never flashes the sign-in page.
 *
 * On no session we bounce to `/sign-in?next=<return path>`, preserving the
 * destination exactly as middleware.ts did (`pathname + search`), so the
 * sign-in form can land the user back where they were headed.
 */
export function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        aria-label="Loading account"
        role="status"
        sx={{ display: "grid", minHeight: "100dvh", placeItems: "center" }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }

  return <Outlet />;
}
